import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useMe } from './useMe';
import type { Rol } from './types';

type Props = {
  children: ReactNode;
  roles?: Rol[];
};

export function RequireAuth({ children, roles }: Props) {
  const location = useLocation();
  const { data, isLoading, isError, error } = useMe();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <i className="ph ph-circle-notch animate-spin" />
          Cargando…
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="max-w-md text-center">
          <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300">
            <i className="ph-fill ph-wifi-slash text-xl" />
          </div>
          <h1 className="text-lg font-semibold">Servicio no disponible</h1>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
            No pudimos conectar con el backend. Verificá tu conexión o contactá al administrador
            del sistema.
          </p>
          <p className="mt-3 text-xs text-slate-400 dark:text-slate-500">{(error as Error)?.message}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-700"
          >
            <i className="ph ph-arrow-clockwise mr-1" />
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  if (roles && !roles.some((r) => data.roles.includes(r))) {
    return <Navigate to="/sin-acceso" replace />;
  }

  return <>{children}</>;
}
